<script setup lang="ts">
    import type { AccessGroupNode, AccessLevel } from '@/js/services/api/role/types';
    import { OrgLevelType, RoleOrgAccessLevel } from '@/js/services/api/role/types';
    import EchoPopover from '../EchoPopover.vue';
    import EchoTooltip from '../EchoTooltip.vue';
    import IconButton from '../IconButton.vue';
    import EnumToggleSwitch from '../EnumToggleSwitch.vue';
    import OrgDataAccessTree from './OrgDataAccessTree.vue';
    import { __ } from '@/js/stores/expressions';
    import { CircleMinusIcon, CirclePlusIcon, InfoIcon } from '@lucide/vue';
    import { computed, watch } from 'vue';

    const props = withDefaults(
        defineProps<{
            // Optional role-baseline lookup; forwarded to the tree to render
            // per-node override badges. Omitted by the role editor (no badges).
            baselineById?: Map<string, AccessGroupNode>;
            // Shows the "team aggregate access" checkbox above the tree.
            showAgentGroups?: boolean;
            // The role's own value of the team-aggregate flag. When provided
            // (user override), the checkbox badges an add/remove against it.
            baselineAgentGroups?: boolean;
            // Who the access applies to — drives the wording of the inline
            // descriptions and help text ("the role" vs "this user"), since the
            // same component is reused by the role admin and the user override.
            subject?: 'role' | 'user';
            // The role's own tree. Selecting Custom resets `groups` to a clone
            // of this, discarding the Full/Agg flag-flips, so customizing starts
            // from the role defaults. Omit to keep `groups` unchanged.
            customBaseline?: AccessGroupNode[];
        }>(),
        { subject: 'role', baselineAgentGroups: undefined }
    );

    const accessLevel = defineModel<AccessLevel>('accessLevel', { required: true });
    const groups = defineModel<AccessGroupNode[]>('groups', { required: true });
    const agentGroups = defineModel<boolean>('agentGroups', { default: false });

    const accessLevelOptions = [
        { value: RoleOrgAccessLevel.Full, label: __('org-access-level-full') },
        { value: RoleOrgAccessLevel.Agg, label: __('org-access-level-aggregated') },
        { value: RoleOrgAccessLevel.Custom, label: __('org-access-level-custom') },
    ];

    const treeDisabled = computed(() => accessLevel.value !== RoleOrgAccessLevel.Custom);

    // Only Custom stores the flag (Full/Agg force it on), so a diff is only
    // meaningful there and only when a role baseline was supplied.
    const agentGroupsDiff = computed<'added' | 'removed' | null>(() => {
        if (props.baselineAgentGroups === undefined || treeDisabled.value) {
            return null;
        }
        if (agentGroups.value && !props.baselineAgentGroups) {
            return 'added';
        }
        if (!agentGroups.value && props.baselineAgentGroups) {
            return 'removed';
        }
        return null;
    });

    const accessLevelDescriptionKey = computed(() => {
        const forUser = props.subject === 'user';
        if (accessLevel.value === RoleOrgAccessLevel.Full) {
            return forUser ? 'org-access-description-full-user' : 'org-access-description-full';
        }
        if (accessLevel.value === RoleOrgAccessLevel.Agg) {
            return forUser ? 'org-access-description-agg-user' : 'org-access-description-agg';
        }
        return forUser ? 'org-access-description-custom-user' : 'org-access-description-custom';
    });

    const infoBulletKeys = computed(() =>
        props.subject === 'user'
            ? ([
                  'user-data-access-info-bullet-1',
                  'user-data-access-info-bullet-2',
                  'user-data-access-info-bullet-3',
              ] as const)
            : ([
                  'role-data-access-info-bullet-1',
                  'role-data-access-info-bullet-2',
                  'role-data-access-info-bullet-3',
              ] as const)
    );

    const setAccessLevel = (
        nodes: AccessGroupNode[],
        includeIndividuals: boolean,
        includeChildGroups: boolean
    ): AccessGroupNode[] =>
        nodes.map((node) => ({
            ...node,
            isSelected: node.orgLevelType === OrgLevelType.Individual ? includeIndividuals : true,
            includeAllIndividuals: node.children.some(
                (c) => c.orgLevelType === OrgLevelType.Individual
            )
                ? includeIndividuals
                : node.includeAllIndividuals,
            includeAllChildGroups:
                node.orgLevelType === OrgLevelType.Group
                    ? includeChildGroups
                    : node.includeAllChildGroups,
            children: node.children.length
                ? setAccessLevel(node.children, includeIndividuals, includeChildGroups)
                : node.children,
        }));

    // JSON round-trip deep-clones the tree (pure JSON-safe data) so the reset
    // doesn't share node references with the baseline.
    const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

    watch(accessLevel, (level) => {
        if (level === RoleOrgAccessLevel.Full) {
            groups.value = setAccessLevel(groups.value, true, true);
        } else if (level === RoleOrgAccessLevel.Agg) {
            groups.value = setAccessLevel(groups.value, false, true);
        } else if (level === RoleOrgAccessLevel.Custom && props.customBaseline) {
            groups.value = deepClone(props.customBaseline);
        }
    });
</script>

<template>
    <div class="flex flex-col gap-4">
        <!-- v-html is safe with __(). Disable the eslint warning for this component. -->
        <!-- eslint-disable vue/no-v-html -->
        <EchoPopover type="text">
            <template #button="{ anchor, togglePopover, buttonAttrs }">
                <div class="flex flex-wrap items-center">
                    <h3 class="heading-sm">{{ __('org-access-level') }}</h3>
                    <div class="ml-1.5 -my-0.5">
                        <IconButton
                            :ref="anchor"
                            size="size-5"
                            area-size="size-6"
                            :aria-label="__('show-info')"
                            v-bind="buttonAttrs"
                            @click="togglePopover"
                        >
                            <InfoIcon class="size-5" />
                        </IconButton>
                    </div>
                </div>
            </template>
            <template #popover>
                <ul class="list-disc pl-5 space-y-2 text-sm">
                    <li v-for="key in infoBulletKeys" :key="key" v-html="__(key)" />
                </ul>
            </template>
        </EchoPopover>
        <div v-if="showAgentGroups" class="flex items-center gap-2">
            <label class="flex items-start py-1 text-body">
                <!-- Full/Agg always grant team-aggregate access at the role level,
                     so the flag is implicitly on and not editable; only Custom
                     stores it. -->
                <input
                    type="checkbox"
                    class="Custom-checkbox"
                    :checked="treeDisabled || agentGroups"
                    :disabled="treeDisabled"
                    @change="agentGroups = ($event.target as HTMLInputElement).checked"
                />
                {{ __('role-includes-agent-groups') }}
            </label>
            <EchoTooltip
                v-if="agentGroupsDiff === 'added'"
                :label="__('user-org-access-added')"
                class="inline-flex"
                tabindex="0"
            >
                <CirclePlusIcon class="size-4 text-primary-carrot" />
            </EchoTooltip>
            <EchoTooltip
                v-else-if="agentGroupsDiff === 'removed'"
                :label="__('user-org-access-removed')"
                class="inline-flex"
                tabindex="0"
            >
                <CircleMinusIcon class="size-4 text-primary-carrot" />
            </EchoTooltip>
        </div>
        <OrgDataAccessTree
            :nodes="groups"
            :disabled="treeDisabled"
            :baseline-by-id="baselineById"
            @update:nodes="groups = $event"
        >
            <template #description>
                <p
                    class="text-caption pb-2 mt-4 mb-4 ml-7.5 border-b border-gray-lightest"
                    v-html="__(accessLevelDescriptionKey)"
                />
            </template>
            <template #root-actions>
                <EnumToggleSwitch
                    id="access-level"
                    v-model="accessLevel"
                    :items="accessLevelOptions"
                />
            </template>
        </OrgDataAccessTree>
    </div>
</template>
